import { useEffect, useMemo, useState } from "react";
import {
  BrainCircuit,
  ChevronDown,
  Factory,
  Landmark,
  LayoutDashboard,
  Search,
  Settings,
  ShoppingCart,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { RouterNavLink } from "../RouterNavLink";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useOrg } from "../../context/OrgContext";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  filterNavGroups,
  findNavGroupIdForPath,
  NAV_GROUPS,
  type NavGroupWithItems,
  type NavItemDef,
} from "../../lib/navigation";
import { LangSwitch } from "./LangSwitch";

interface SidebarProps {
  onNavigate?: () => void;
}

const GROUP_ICONS: Record<string, LucideIcon> = {
  home: LayoutDashboard,
  sales_ops: ShoppingCart,
  finance: Landmark,
  production: Factory,
  hr: Users,
  intelligence: BrainCircuit,
  system: Settings,
};

const NAV_GROUPS_DEFAULT_OPEN = new Set(["home", "sales_ops"]);

function initialExpanded(
  groups: NavGroupWithItems[],
  activeGroupId: string | null,
): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const group of groups) {
    const meta = NAV_GROUPS.find((g) => g.id === group.id);
    const defaultOpen =
      meta?.defaultOpen ?? NAV_GROUPS_DEFAULT_OPEN.has(group.id);
    state[group.id] = group.id === activeGroupId || defaultOpen;
  }
  return state;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { profile, logout } = useAuth();
  const { orgName, orgId, baseCurrency, verticalCode, isLoading } = useOrg();
  const { isTrial } = useSubscription();
  const [search, setSearch] = useState("");

  const roleKey = profile?.roles?.join(",") ?? "";
  const groups = useMemo(() => {
    if (!roleKey || !profile) return [];
    return filterNavGroups(profile.roles, verticalCode);
  }, [roleKey, verticalCode, profile]);
  const activeGroupId = findNavGroupIdForPath(pathname);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    initialExpanded(groups, activeGroupId),
  );

  // Open the group for the current route only — avoid re-running when `groups` reference changes.
  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) =>
      prev[activeGroupId] ? prev : { ...prev, [activeGroupId]: true },
    );
  }, [activeGroupId]);

  useEffect(() => {
    if (groups.length === 0) return;
    setExpanded((prev) =>
      Object.keys(prev).length > 0 ? prev : initialExpanded(groups, activeGroupId),
    );
  }, [roleKey, verticalCode, groups.length, activeGroupId]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          t(item.labelKey).toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, search, t]);

  function toggleGroup(groupId: string) {
    setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }

  return (
    <aside className="flex h-full min-h-screen w-72 flex-col border-slate-200 bg-white ltr:border-r rtl:border-l">
      <div className="border-b border-slate-100 px-4 py-4">
        <div className="flex items-center gap-3">
          <img
            src="/godo-logo.png?v=20260604"
            alt="Go.Do ERP"
            className="h-10 w-10 shrink-0 rounded-lg object-contain"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-godo-primary">
              {orgName ?? t("labels.brand_name")}
            </p>
            <p className="truncate text-xs text-godo-muted-green">
              {isLoading
                ? t("labels.loading")
                : baseCurrency
                  ? `${t("labels.organization")} · ${baseCurrency}`
                  : t("labels.brand_sub")}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <LangSwitch />
        </div>
      </div>

      <div className="border-b border-slate-100 px-3 py-3">
        <label className="relative block">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-godo-muted-green ltr:left-3 rtl:right-3"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("labels.nav_search_placeholder")}
            className="w-full rounded-lg border border-godo-input-border bg-godo-input-bg py-2 text-sm text-godo-primary outline-none focus:border-godo-accent focus:ring-2 focus:ring-godo-accent/20 ltr:pl-9 ltr:pr-3 rtl:pl-3 rtl:pr-9"
          />
        </label>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label={t("labels.nav_menu")}>
        {filteredGroups.length === 0 ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {search.trim() ? t("labels.nav_search_empty") : t("labels.nav_empty_hint")}
          </p>
        ) : (
          <ul className="space-y-2">
            {filteredGroups.map((group) => (
              <NavGroupSection
                key={group.id}
                group={group}
                isOpen={search.trim() ? true : (expanded[group.id] ?? false)}
                onToggle={() => toggleGroup(group.id)}
                onNavigate={onNavigate}
                forceFlat={!!search.trim()}
              />
            ))}
          </ul>
        )}
      </nav>

      <div className="border-t border-slate-100 px-4 py-4">
        {isTrial && (
          <span className="mb-3 inline-flex rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
            {t("labels.free_trial")}
          </span>
        )}
        <p className="truncate text-sm font-medium text-godo-primary">
          {profile?.displayName ?? t("labels.default_user")}
        </p>
        <p className="mt-0.5 truncate text-xs text-godo-muted-green">
          {orgId ?? profile?.orgId ?? "—"}
        </p>
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          className="mt-3 w-full rounded-lg border border-godo-input-border px-3 py-2 text-sm text-godo-mint transition hover:bg-godo-input-bg"
        >
          {t("labels.nav_logout")}
        </button>
      </div>
    </aside>
  );
}

function NavGroupSection({
  group,
  isOpen,
  onToggle,
  onNavigate,
  forceFlat,
}: {
  group: NavGroupWithItems;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  forceFlat: boolean;
}) {
  const { t } = useTranslation();
  const Icon = GROUP_ICONS[group.id] ?? LayoutDashboard;
  const singleItem = group.items.length === 1;

  if (singleItem && group.id === "home") {
    const item = group.items[0]!;
    return (
      <li>
        <NavItemLink item={item} onNavigate={onNavigate} prominent />
      </li>
    );
  }

  return (
    <li className="rounded-xl border border-slate-100/80 bg-slate-50/50">
      <button
        type="button"
        onClick={() => {
          if (!forceFlat) onToggle();
        }}
        className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-start text-sm font-bold text-godo-primary transition hover:bg-white/80 ${
          forceFlat ? "cursor-default" : "cursor-pointer"
        }`}
        aria-expanded={isOpen}
      >
        <Icon className="h-4 w-4 shrink-0 text-godo-accent" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t(group.labelKey)}</span>
        <span className="rounded-full bg-godo-accent/10 px-2 py-0.5 text-[10px] font-semibold text-godo-accent">
          {group.items.length}
        </span>
        {!forceFlat && (
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-godo-muted-green transition ${isOpen ? "rotate-180" : ""}`}
            aria-hidden
          />
        )}
      </button>
      {isOpen && (
        <ul className="space-y-0.5 px-2 pb-2">
          {group.items.map((item) => (
            <li key={item.id}>
              <NavItemLink item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function NavItemLink({
  item,
  onNavigate,
  prominent = false,
}: {
  item: NavItemDef;
  onNavigate?: () => void;
  prominent?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <RouterNavLink
      to={item.to}
      end={item.end}
      onNavigate={onNavigate}
      className={({ isActive }) =>
        `block rounded-lg px-3 py-2 text-sm transition ${
          prominent
            ? isActive
              ? "bg-godo-accent font-bold text-white shadow-sm"
              : "bg-white font-semibold text-godo-accent shadow-sm hover:bg-godo-accent/10"
            : isActive
              ? "bg-godo-accent/15 font-medium text-godo-accent"
              : "text-godo-mint hover:bg-white hover:text-godo-primary"
        }`
      }
    >
      {t(item.labelKey)}
    </RouterNavLink>
  );
}
